import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  DialogComponent,
  ModalComponent,
  NavBar,
  SearchInput,
  UploadFileForm,
} from "../../../components";
import { useDate, useFetch, useRequest } from "../../../hooks";
import {
  ClientProps,
  DocumentProps,
  UserDataProps,
  WellProps,
} from "../../../interfaces/interfaces";
import { DataGrid, GridColDef, GridDeleteIcon } from "@mui/x-data-grid";
import {
  Autocomplete,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import styles from "../../main.module.sass";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { useFilter } from "../../../hooks/useFilter";
import DataContext from "../../../context/DataContext";

interface Props extends UserDataProps {
  id: number;
}

interface FetchResponse {
  success?: boolean;
  data: Props[];
}

interface PullingProps {
  id: number;
  active: boolean;
  customName: string;
  date: string;
  idClient: number;
  idWell: number;
  path: string;
}

const dataGridStyles = {
  border: "1px solid rgb(90,100,119)",
  borderRadius: "10px",
  padding: "1%",
  height: "75vh",
  width: "90%",
};

const iconButtonStyles = {
  backgroundColor: "rgb(90,100,119)",
  color: "white",
  marginRight: "5px",
  borderRadius: "10px",
  "&:hover": {
    backgroundColor: "rgb(251,171,53)",
  },
};

export const PullingReportAdmin = () => {
  const { data, isLoading } = useFetch<FetchResponse>("client");
  const { handleRequest } = useRequest();
  const { getDateFromString } = useDate();
  const [wellsByClient, setWellsByClient] = useState([]);
  const [pullQuery, setPullQuery] = useState("");
  const [dataPullingReport, setDataPullingReport] = useState<PullingProps[]>(
    [],
  );
  const { filteredData: pullingData } = useFilter<PullingProps>(
    dataPullingReport,
    {
      query: pullQuery,
      field: "customName",
    },
  );
  const [idClientSelected, setIdClientSelected] = useState(0);
  const [idWellSelected, setIdWellSelected] = useState(null);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const matches = useMediaQuery("(min-width:600px)");
  const {
    clientOptions,
    wellOptions,
    fetchDataWells,
    fetchDataClient,
    onDeleteDocument,
  } = useContext(DataContext);
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsError(false);
      setIsSuccess(false);
    }, 4000);
  }, [isError, isSuccess]);

  useEffect(() => {
    fetchDataClient();
  }, []);

  const fetchWellsByClientId = (idClient: number) => {
    setIdClientSelected(idClient);
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest({ endpoint: `/well/client/${idClient}`, options })
      .then((response) => {
        setWellsByClient(response.data);
      })
      .catch((e) => setIsError(true));
  };

  const fetchPullingReports = (idWell: number) => {
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest({ endpoint: `pulling-list/${idWell}`, options })
      .then((response) => {
        if (response.success) {
          setDataPullingReport(response.data);
        } else {
          setIsError(true);
        }
      })

      .catch((e) => setIsError(true));
  };

  const fetchPullingByKey = (key: string, name: string, download: boolean) => {
    if (key) {
      setIsCreatingReport(true);
      let options: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key }),
        headers: {
          responseType: "Buffer",
        },
      };
      handleRequest({ endpoint: "pdf-pulling-adm", options })
        .then((response) => {
          if (response.success) {
            createPdfFile(response.data, name, download);
          } else {
            setIsError(true);
          }
          setIsCreatingReport(false);
        })
        .catch((e) => {
          setIsError(true);
          setIsCreatingReport(false);
        });
    }
  };

  const createPdfFile = (
    data: { type: string; data: Buffer },
    name: string,
    download: boolean,
  ) => {
    try {
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(data.data).buffer], {
          type: "application/pdf;charset=utf-8",
        }),
      );
      if (download) {
        var link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
      } else {
        window.open(url);
      }
      setIsCreatingReport(false);
    } catch (e) {
      setIsCreatingReport(false);
      setIsError(true);
    }
  };

  const handleUploadPulling = async ({
    client,
    well,
    file,
    customName,
    installationDate,
  }: DocumentProps) => {
    try {
      setIsCreatingReport(true);
      setIsModalOpen(false);
      let formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      formData.append("type", "pulling");
      customName && formData.append("customName", customName);
      installationDate &&
        formData.append("installationDate", installationDate.toISOString());
      formData.append("file", file, file.name);
      const response = await fetch(
        process.env.REACT_APP_DEV_API + "/pulling-upload",
        {
          method: "POST",
          body: formData,
        },
      );
      const res = await response.json();
      if (res.success) {
        setIsCreatingReport(false);
        idWellSelected && fetchPullingReports(idWellSelected);
        setIsSuccess(true);
      } else {
        setIsCreatingReport(false);
        setIsError(true);
      }
    } catch (e) {
      setIsCreatingReport(false);
      setIsError(true);
    }
  };

  const columnsPullingReport: GridColDef[] = [
    {
      field: "edit",
      headerName: "Actions",
      width: 180,
      renderHeader: () => <></>,
      renderCell: (params: any) => {
        return (
          <>
            <Tooltip title="Open in a new Page">
              <IconButton
                sx={iconButtonStyles}
                size="small"
                onClick={() =>
                  fetchPullingByKey(
                    params.row.path,
                    params.row.customName,
                    false,
                  )
                }
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                sx={iconButtonStyles}
                size="small"
                onClick={() =>
                  fetchPullingByKey(
                    params.row.path,
                    params.row.customName,
                    true,
                  )
                }
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  setIdToDelete(params.row?.id);
                  setIsOpenAlert(true);
                }}
                sx={iconButtonStyles}
                size="small"
              >
                <GridDeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
      disableColumnMenu: true,
    },
    {
      field: "customName",
      headerName: "Name",
      renderHeader: () => <strong>{"Pulling Report"}</strong>,
      sortable: true,
      width: 300,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.customName}>
            <span>{params.row.customName}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "date",
      headerName: "Date",
      renderHeader: () => <strong>{"Date"}</strong>,
      sortable: true,
      width: 200,
      renderCell: (params: any) => {
        return <span>{getDateFromString(params.row.date)}</span>;
      },
    },
    {
      field: "installationDate",
      headerName: "Installation Date",
      renderHeader: () => <strong>{" Installation Date"}</strong>,
      sortable: true,
      width: 200,
      renderCell: (params: any) => {
        return (
          <span>
            {params.row.installationDate
              ? getDateFromString(params.row.installationDate)
              : ""}
          </span>
        );
      },
    },
  ];

  const buttons = [
    {
      title: "Upload",
      action: () => setIsModalOpen(true),
      icon: <DriveFolderUploadIcon />,
      disabled: false,
    },
  ];

  const defaultProps = {
    options: clientOptions,
    getOptionLabel: (option: ClientProps) => option.name,
  };

  const onChangeClient = (clientSelected: ClientProps) => {
    fetchDataWells(clientSelected.id);
    // setValues((prevState) => {
    //   return {
    //     ...prevState,
    //     client: clientSelected,
    //   };
    // });
  };

  const onChangeWell = (wellSelected: WellProps) => {
    // setValues((prevState) => {
    //   return {
    //     ...prevState,
    //     well: wellSelected,
    //   };
    // });
  };

  const defaultPropsWellSelector = {
    options: wellOptions,
    getOptionLabel: (option: WellProps) => option.name,
  };
  return (
    <>
      <NavBar title="Pulling Reports" buttons={buttons} />
      <Grid
        container
        sx={{ marginTop: "2vh", marginLeft: "2vh" }}
        alignItems="center"
      >
        <Grid item xs={12} md={4} lg={4} justifyContent="center">
          <Autocomplete
            id="search-selector"
            // value={values.client}
            isOptionEqualToValue={(option, value) => {
              return true;
            }}
            style={{ width: "80%", marginTop: "20px" }}
            onChange={(_, newValue) => newValue && onChangeClient(newValue)}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              );
            }}
            {...defaultProps}
            disablePortal
            className={styles.formFieldModalCustom}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <Autocomplete
            id="well-selector"
            isOptionEqualToValue={(option, value) => {
              return true;
            }}
            style={{ width: "80%", marginTop: "20px" }}
            onChange={(_, newValue) => {
              if (newValue) {
                fetchPullingReports(newValue.id as any);
                setIdWellSelected(newValue.id as any);
              }
            }}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              );
            }}
            {...defaultPropsWellSelector}
            disablePortal
            className={styles.formFieldModalCustom}
            renderInput={(params) => <TextField {...params} label="Well" />}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <SearchInput
            label="Search Pulling"
            value={pullQuery}
            setValue={setPullQuery}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <DataGrid
            density={matches ? "standard" : "compact"}
            style={dataGridStyles}
            rows={pullingData}
            columns={columnsPullingReport}
            className={styles.pullingTable}
            loading={isLoading}
            autoPageSize
            pagination
            slots={{
              noRowsOverlay: () => (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <>
                    Filter By Client and Well to see Pulling Reports available
                  </>
                </div>
              ),
            }}
          />
        </Grid>
      </Grid>

      {isCreatingReport && <div className={styles.backdrop}></div>}
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isModalOpen && data && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={clientOptions}
              wellOptions={wellOptions}
              type="pulling"
              fetchDataWells={fetchDataWells}
              onUploadFile={(values: DocumentProps) => {
                handleUploadPulling(values);
              }}
              onCancel={() => {
                setIsModalOpen(false);
              }}
            />
          }
        />
      )}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={
            <div>
              Are you sure you want to remove this Pulling Report from the
              Cloud?
            </div>
          }
          onAccept={() => {
            onDeleteDocument("pulling-file", idToDelete, () => {
              idWellSelected && fetchPullingReports(idWellSelected);
            });
            setIdToDelete("");
            setIsOpenAlert(false);
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );
};
